import React, { useState, useEffect } from 'react';
import { filterUsers, getAllUserIds } from '../utils/taskUtils';
import './UserSelectSlideOver.css';

const UserSelectSlideOver = ({ 
    show, 
    users, 
    selectedUsers, 
    onChange, 
    onClose 
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Prevent body scroll when slide-over is open
    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [show]);

    if (!show) return null;

    const handleSelectAll = () => {
        const allUserIds = getAllUserIds(users);
        onChange(allUserIds);
    };

    const handleDeselectAll = () => {
        onChange([]);
    };

    const getFilteredUsers = () => {
        return filterUsers(users, searchTerm);
    };

    const handleToggleUser = (userId) => {
        if (selectedUsers.includes(userId)) {
            onChange(selectedUsers.filter(id => id !== userId));
        } else {
            onChange([...selectedUsers, userId]);
        }
    };

    const handleToggleDepartment = (deptUserIds, allSelected) => {
        if (allSelected) {
            onChange(selectedUsers.filter(id => !deptUserIds.includes(id)));
        } else {
            onChange([...new Set([...selectedUsers, ...deptUserIds])]);
        }
    };

    return (
        <div className="slideover-overlay" onClick={onClose}>
            <div className="slideover-panel" onClick={(e) => e.stopPropagation()}>
                <div className="slideover-header">
                    <div className="slideover-title-section">
                        <h2>Select Users</h2>
                        <span className="selected-count">
                            {selectedUsers.length} selected
                        </span>
                    </div>
                    <button 
                        className="slideover-close-btn" 
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="slideover-search">
                    <input
                        type="text"
                        placeholder="Search by name, role, or department..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="slideover-search-input"
                    />
                    {searchTerm && (
                        <button 
                            className="clear-search-btn"
                            onClick={() => setSearchTerm('')}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="slideover-actions">
                    <button 
                        className="btn-select-all"
                        onClick={handleSelectAll}
                    >
                        Select All
                    </button>
                    <button 
                        className="btn-deselect-all"
                        onClick={handleDeselectAll}
                    >
                        Clear All
                    </button>
                </div>

                <div className="slideover-content">
                    {Object.keys(getFilteredUsers()).sort().map(department => {
                        const deptUsers = getFilteredUsers()[department];
                        if (!deptUsers || deptUsers.length === 0) return null;
                        
                        const selectedInDept = deptUsers.filter(u => selectedUsers.includes(u._id)).length;
                        const allSelected = selectedInDept === deptUsers.length;
                        const someSelected = selectedInDept > 0 && selectedInDept < deptUsers.length;
                        
                        return (
                            <div key={department} className="slideover-department">
                                <div className="department-header">
                                    <label className="department-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => {
                                                if (el) el.indeterminate = someSelected;
                                            }}
                                            onChange={() => {
                                                const deptUserIds = deptUsers.map(u => u._id);
                                                handleToggleDepartment(deptUserIds, allSelected);
                                            }}
                                        />
                                        <span className="department-name">{department}</span>
                                        <span className="department-count">({selectedInDept}/{deptUsers.length})</span>
                                    </label>
                                </div>
                                <div className="department-users">
                                    {deptUsers.map(u => (
                                        <label key={u._id} className="user-item">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(u._id)}
                                                onChange={() => handleToggleUser(u._id)}
                                            />
                                            <div className="user-info">
                                                <span className="user-name">{u.fullName}</span>
                                                <span className="user-role">{u.role}</span>
                                            </div>
                                            {selectedUsers.includes(u._id) && (
                                                <span className="user-selected-icon">✓</span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {Object.keys(getFilteredUsers()).length === 0 && (
                        <div className="no-results">
                            <p>No users found matching "{searchTerm}"</p>
                        </div>
                    )}
                </div>

                <div className="slideover-footer">
                    <button 
                        className="btn-secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn-primary"
                        onClick={onClose}
                        disabled={selectedUsers.length === 0}
                    >
                        Done ({selectedUsers.length} selected)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserSelectSlideOver;
